import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon';
import { ExpeditionStatusEnum, LocationPreviewV4OutDTO } from '@api/ringil-api.v2';
import InputTimezone from '@components/ringil3/Input/InputTimezone/InputTimezone.tsx';
import { ApiContext } from '@api/api';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { Field } from '@components/framework/formfield/Field.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import { useApplyResolvedTimezone } from '@hooks/useResolveTimezone/useResolveTimezone';
import { findErrorMessage } from '@utils/validationUtils.ts';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import RingilButton from '@components/framework/buttons/RingilButton.tsx';
import SvgCoButtonDelete from '@icons/CoButtonDelete.tsx';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon.tsx';
import SvgIconPlus from '@components/framework/orders/IconPlus.tsx';
import { ManualAddress } from '@app/pages/expedition/create/types.ts';

interface ComponentProps {
  applicationId: string;
  expeditionId: string;
  locationId: string;
  onContinue: () => void;
  location: LocationPreviewV4OutDTO;
  expeditionStatus: ExpeditionStatusEnum;
  canEdit: boolean;
}

interface Form extends ManualAddress {
  loadingReference?: string;
}

export const useCreateEditManualExpeditionLocation = () => {
  const [visible, setVisible] = useState(false);

  const EditExpeditionManualLocationSidebar: React.FC<ComponentProps> = ({ onContinue, applicationId, locationId, expeditionId, location, expeditionStatus, canEdit }) => {
    const { tr } = useTranslationLgs();
    const { editManualLocationExpedition } = useContext(ApiContext);
    const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
    const [loading, setLoading] = useState(false);
    const [resolvedTimezoneId, setResolvedTimezoneId] = useState<string | null>(null);
    const timezoneEditable = canEdit && expeditionStatus === ExpeditionStatusEnum.NEW;

    const save = (data: Form) => {
      setLoading(true);
      editManualLocationExpedition(
        expeditionId,
        locationId,
        {
          selectedPerson:
            data.mainContactEmail || data.mainContactPerson || data.mainContactPhone
              ? {
                  contactEmail: data.mainContactEmail || '',
                  contactName: data.mainContactPerson || '',
                  contactPhone: data.mainContactPhone || '',
                }
              : null,
          address: { name: data.name, city: data.city, country: data.countryCode3, postalCode: data.postalCode, streetNr: data.streetNr },
          loadingReference: data.loadingReference,
          notifySelectedPerson: data.notifySelectedPerson,
          additionalContacts: data.additionalPersons?.map(t => ({
            contactEmail: t.email || '',
            contactName: t.name || '',
          })),
          timezoneId: data.timezoneId,
        },
        () => {
          setLoading(false);
          setVisible(false);
          onContinue();
        },
      );
    };

    const schema = () =>
      yup.object().shape({
        timezoneId: yup
          .string()
          .test(
            'invalid',
            tr('validation.invalid', 'Value is invalid'),
            val => !resolvedTimezoneId || !val || val === resolvedTimezoneId,
          ),
      });
    const form = useForm<Form>(
      schema,
      {
        loadingReference: location.loadingReference,
        mainContactEmail: location.selectedPerson?.contactEmail,
        mainContactPerson: location.selectedPerson?.contactName,
        mainContactPhone: location.selectedPerson?.contactPhone,
        name: location.name,
        postalCode: location.postalCode,
        countryCode3: location.country,
        city: location.city,
        streetNr: location.streetNr,
        additionalPersons: location.additionalContacts.map(t => ({ name: t.contactName, email: t.contactEmail })),
        notifySelectedPerson: location.notifySelectedPerson,
        timezoneId: location.timezoneId,
      },
      d => save(d),
      true,
      true,
      undefined,
      'none',
    );

    useEffect(() => {
      if (isNotNullOrUndefined(location) && visible === true) {
        form.overwriteValues({
          mainContactEmail: location.selectedPerson?.contactEmail,
          mainContactPerson: location.selectedPerson?.contactName,
          mainContactPhone: location.selectedPerson?.contactPhone,
          name: location.name,
          postalCode: location.postalCode,
          countryCode3: location.country,
          city: location.city,
          streetNr: location.streetNr,
          loadingReference: location.loadingReference,
          additionalPersons: location.additionalContacts.map(t => ({ name: t.contactName, email: t.contactEmail })),
          notifySelectedPerson: location.notifySelectedPerson,
          timezoneId: location.timezoneId,
        });
      }
    }, [location]);

    useApplyResolvedTimezone(
      {
        countryCode3: form.values.countryCode3,
        city: form.values.city,
        postalCode: form.values.postalCode,
      },
      resolvedTz => {
        setResolvedTimezoneId(resolvedTz ?? null);
        if (timezoneEditable && resolvedTz) {
          form.useProduce(d => {
            d.timezoneId = resolvedTz;
          });
        }
      },
    );

    useEffect(() => {
      if (visible) form.validateForm(form.values);
    }, [resolvedTimezoneId]);

    if (!visible) return null;

    return (
      <>
        <RingilSidebar
          visible={visible}
          onBack={() => setVisible(false)}
          onContinue={() => form.validateAndSend()}
          heading={tr('EditManualLocationAndContactSidebar.zmenaNaLokalite', 'Změna na lokalitě')}
          loading={loading}
          disableSaveButton={!form.isValid}
        >
          <div className={'flex flex-col gap-4'}>
            <BackendValidationComponent />
            <VerticalFieldAndValueWithIcon
              label={tr(`AddMoreCarriers.forExpedition`, `For expedition`)}
              valueText={applicationId}
              iconPath={'/images/icons/ringil3/forExpedition.svg'}
            />

            <>
              <Field required label={tr('EditManualLocationAndContactSidebar.nazevLokace', 'Název lokace')}>
                <RingilInputText
                  value={form.values.name}
                  fullWidth
                  onChange={v =>
                    form.useProduce(d => {
                      d.name = v;
                    })
                  }
                />
              </Field>
              <div className={'grid grid-cols-2 gap-4'}>
                <Field label={tr('EditManualLocationAndContactSidebar.psc', 'PSČ')}>
                  <RingilInputText
                    value={form.values.postalCode}
                    onChange={v =>
                      form.useProduce(draft => {
                        draft.postalCode = v;
                      })
                    }
                  />
                </Field>
                <Field label={tr('EditManualLocationAndContactSidebar.zeme', 'Země')}>
                  <InputCountry
                    value={form.values.countryCode3}
                    onChange={v => {
                      form.useProduce(d => {
                        d.countryCode3 = v;
                      });
                    }}
                  />
                </Field>
              </div>
              <Field
                label={tr('EditManualLocationAndContactSidebar.timezone', 'Časové pásmo')}
                disabled={!timezoneEditable}
                errorMessage={findErrorMessage(form.validationErrors, 'timezoneId', tr)}
              >
                <InputTimezone
                  value={form.values.timezoneId}
                  onChange={v =>
                    form.useProduce(d => {
                      d.timezoneId = v;
                    })
                  }
                  dataTestId={'manualLocationTimezone'}
                />
              </Field>
              <Field required label={tr('EditManualLocationAndContactSidebar.mesto', 'Město')}>
                <RingilInputText
                  value={form.values.city}
                  fullWidth
                  onChange={v =>
                    form.useProduce(d => {
                      d.city = v;
                    })
                  }
                />
              </Field>
              <Field required label={tr('EditManualLocationAndContactSidebar.uliceCp', 'Ulice, č.p')}>
                <RingilInputText
                  value={form.values.streetNr}
                  fullWidth
                  onChange={v =>
                    form.useProduce(d => {
                      d.streetNr = v;
                    })
                  }
                />
              </Field>
              <COHeadline variant={'h4'} title={tr('EditManualLocationAndContactSidebar.kontakty', 'Kontakty')} />
              <Field label={tr('EditManualLocationAndContactSidebar.hlavniKontaktJmeno', 'Hlavní kontakt jméno')}>
                <RingilInputText
                  value={form.values.mainContactPerson}
                  fullWidth
                  onChange={v =>
                    form.useProduce(d => {
                      d.mainContactPerson = v;
                    })
                  }
                />
              </Field>
              <Field label={tr('EditManualLocationAndContactSidebar.hlavniKontaktniTelefon', 'Hlavní kontaktní telefon')}>
                <RingilInputText
                  value={form.values.mainContactPhone}
                  fullWidth
                  onChange={v =>
                    form.useProduce(d => {
                      d.mainContactPhone = v;
                    })
                  }
                />
              </Field>
              <Field label={tr('EditManualLocationAndContactSidebar.hlavniKontaktniEmail', 'Hlavní kontaktní e-mail')}>
                <RingilInputText
                  value={form.values.mainContactEmail}
                  fullWidth
                  onChange={v =>
                    form.useProduce(d => {
                      d.mainContactEmail = v;
                    })
                  }
                />
              </Field>

              <COHeadline variant={'h4'} title={tr('EditManualLocationAndContactSidebar.notifikovatEmailemOPreprave', 'Notifikovat e-mailem o přepravě')} />

              {form.values.mainContactEmail && form.values.mainContactPerson && (
                <InputCheckbox
                  value={form.values.notifySelectedPerson}
                  label={tr('EditManualLocationAndContactSidebar.hlavniKontakt', 'Hlavní kontakt')}
                  note={form.values.mainContactEmail}
                  onChange={(_, v) =>
                    form.useProduce(d => {
                      d.notifySelectedPerson = v;
                    })
                  }
                />
              )}
              {form.values.additionalPersons?.map((person, personIndex) => {
                return (
                  <React.Fragment key={personIndex}>
                    <div className={'grid grid-cols-2 gap-4'}>
                      <Field label={tr('EditManualLocationAndContactSidebar.jmenoOsoby', 'Jméno osoby')} required>
                        <RingilInputText
                          value={person.name}
                          onChange={v =>
                            form.useProduce(draft => {
                              draft.additionalPersons[personIndex].name = v;
                            })
                          }
                        />
                      </Field>
                      <Field
                        label={tr('EditManualLocationAndContactSidebar.notifikovatEmailemOPreprave', 'Notifikovat e-mailem o přepravě')}
                        required
                        actionButtons={
                          <RingilButton
                            mode={'rect-small'}
                            leftIcon={<SvgCoButtonDelete />}
                            onClick={() => {
                              form.useProduce(draft => {
                                draft.additionalPersons = draft.additionalPersons.filter((_, i) => i !== personIndex);
                              });
                            }}
                          />
                        }
                      >
                        <RingilInputText
                          value={person.email}
                          onChange={v =>
                            form.useProduce(d => {
                              d.additionalPersons[personIndex].email = v;
                            })
                          }
                        />
                      </Field>
                    </div>
                  </React.Fragment>
                );
              })}
              <LinkWithIcon
                label={tr('EditManualLocationAndContactSidebar.notifikovatDalsiEmail', 'Notifikovat další e-mail')}
                icon={<SvgIconPlus />}
                onClick={() => {
                  form.useProduce(draft => {
                    if (draft.additionalPersons == null) {
                      draft.additionalPersons = [];
                    }
                    draft.additionalPersons.push({
                      name: '',
                      email: '',
                    });
                  });
                }}
              />
              <Field label={tr('EditManualLocationAndContactSidebar.referenceProNakladku', 'Reference pro nakládku')}>
                <RingilInputText fullWidth value={form.values.loadingReference} onChange={v => form.setFieldValue('loadingReference', v)} />
              </Field>
            </>
          </div>
        </RingilSidebar>
      </>
    );
  };

  return { EditExpeditionManualLocationSidebar, setVisible, visible };
};
